import * as Sentry from '@sentry/browser';
import {
  ReplicantError,
  ReplicantErrorCode,
  ReplicantErrorSubCode,
} from '@play-co/replicant';
import { analytics } from '@play-co/gcinstant';
import { tryLocalStorage } from './tryLocalStorage';
import { captureGenericError } from './sentry';

const AUTH_RECOVERY_RETRY_KEY = 'auth_recovery_retry';

// Max age for the retry flag in milliseconds (60 seconds).
// If the redirect takes longer than this, the flag expires
// and the next load gets a fresh recovery attempt.
const AUTH_RECOVERY_MAX_AGE_MS = 60_000;

/**
 * Check if an error is an authorization error that can be recovered by
 * forcing LIFF re-authentication: LIFF can return a stale cached access token
 * (`invalid_signature`) or an expired cached ID token (`token_expired`).
 */
export function isRecoverableAuthError(error: unknown): boolean {
  return (
    error instanceof ReplicantError &&
    error.code === ReplicantErrorCode.authorization_error &&
    (error.subCode === ReplicantErrorSubCode.invalid_signature ||
      error.subCode === ReplicantErrorSubCode.token_expired)
  );
}

/**
 * Check if LIFF re-authentication is available in the current environment.
 */
export function isLiffReauthAvailable(): boolean {
  return !!window.liff;
}

/**
 * Read the retry flag from localStorage. Returns true if the flag
 * exists and is within the TTL window.
 */
function hasRetryFlag(): boolean {
  try {
    const value = tryLocalStorage()?.getItem(AUTH_RECOVERY_RETRY_KEY);
    if (!value) return false;

    const timestamp = parseInt(value, 10);
    if (isNaN(timestamp)) return false;

    const age = Date.now() - timestamp;
    if (age >= AUTH_RECOVERY_MAX_AGE_MS) {
      Sentry.addBreadcrumb({
        category: 'auth-recovery',
        message: 'Retry flag expired',
        level: 'warning',
        data: { age, maxAge: AUTH_RECOVERY_MAX_AGE_MS },
      });
      return false;
    }

    return true;
  } catch (error) {
    captureGenericError('auth-recovery: hasRetryFlag storage error', error);
    return false;
  }
}

/**
 * Set the retry flag in localStorage with the current timestamp.
 * Returns true if the flag was successfully written.
 */
function setRetryFlag(): boolean {
  try {
    const storage = tryLocalStorage();
    if (!storage) {
      Sentry.addBreadcrumb({
        category: 'auth-recovery',
        message: 'localStorage unavailable, cannot persist retry flag',
        level: 'warning',
      });
      return false;
    }

    storage.setItem(AUTH_RECOVERY_RETRY_KEY, String(Date.now()));
    return true;
  } catch (error) {
    captureGenericError('auth-recovery: setRetryFlag storage error', error);
    return false;
  }
}

/**
 * Remove the retry flag from localStorage.
 */
function clearRetryFlag(): void {
  try {
    tryLocalStorage()?.removeItem(AUTH_RECOVERY_RETRY_KEY);
  } catch (error) {
    captureGenericError('auth-recovery: clearRetryFlag storage error', error);
  }
}

/**
 * Attempt to recover from a recoverable auth error by forcing LIFF
 * re-authentication. Returns true if a redirect was initiated.
 *
 * Uses localStorage with a TTL to prevent infinite redirect loops:
 * - First attempt: sets flag, tracks analytics, flushes, redirects via LIFF
 * - Second attempt (flag exists): clears flag and returns false
 * - If storage is unavailable: returns false (no redirect, avoids loops)
 */
export async function attemptAuthRecovery(): Promise<boolean> {
  if (hasRetryFlag()) {
    // Already retried once — clear flag and let the error propagate.
    Sentry.addBreadcrumb({
      category: 'auth-recovery',
      message: 'Recovery already attempted, aborting to prevent loop',
      level: 'warning',
    });
    clearRetryFlag();
    return false;
  }

  // CRITICAL: If we can't persist the flag, we must NOT redirect.
  // Without the flag, the next load would have no way to detect
  // a previous attempt, causing an infinite redirect loop.
  if (!setRetryFlag()) {
    Sentry.addBreadcrumb({
      category: 'auth-recovery',
      message: 'Failed to persist retry flag, aborting redirect',
      level: 'error',
    });
    return false;
  }

  // Best-effort flush — do not let a network failure block the redirect.
  try {
    Sentry.addBreadcrumb({
      category: 'auth-recovery',
      message: 'Initiating LIFF re-auth redirect',
      level: 'info',
      data: { redirectUri: window.location.href },
    });

    await analytics.flush();
  } catch (flushError) {
    captureGenericError('auth-recovery: analytics flush failed', flushError);
  }

  // Initiate LIFF re-authentication, which will redirect the page.
  try {
    window.liff.logout();
    window.liff.login({ redirectUri: window.location.href });
  } catch (liffError) {
    // If LIFF throws, clear the retry flag so the next load gets a fresh
    // recovery attempt instead of thinking one already happened.
    captureGenericError('auth-recovery: LIFF redirect failed', liffError);
    clearRetryFlag();
    return false;
  }

  return true;
}

/**
 * Check if the current session recovered from a recoverable auth error.
 * Should be called after successful Replicant client creation.
 * Clears the retry flag and tracks success if recovery was detected.
 */
export function checkAndTrackRecoverySuccess(): void {
  try {
    if (hasRetryFlag()) {
      Sentry.addBreadcrumb({
        category: 'auth-recovery',
        message: 'Recovery succeeded after LIFF re-auth',
        level: 'info',
      });
      clearRetryFlag();
    }
  } catch (error) {
    captureGenericError(
      'auth-recovery: checkAndTrackRecoverySuccess error',
      error,
    );
  }
}
